import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusContext } from './Status';
import { ACUVERSE_LOGO_V101, useAcuverseLogoV101 } from './acuverseLogoV101';

export const normalizeSystemNameV9 = (value) =>
  typeof value === 'string' ? value.trim() : '';

const BrandingContextV9 = createContext({
  systemName: '',
  logo: ACUVERSE_LOGO_V101,
  onLogoError: () => {},
});

export const SystemBrandingProviderV9 = ({ children }) => {
  const [statusState, statusDispatch] = useContext(StatusContext);
  const { i18n } = useTranslation();
  const systemName = normalizeSystemNameV9(statusState.status?.system_name);
  const { logo, onLogoError } = useAcuverseLogoV101(statusState.status?.logo);
  const branding = useMemo(
    () => ({ systemName, logo, onLogoError }),
    [systemName, logo, onLogoError]
  );

  useEffect(() => {
    document.title = systemName;
    if (i18n.options?.interpolation) {
      i18n.options.interpolation.defaultVariables = {
        ...i18n.options.interpolation.defaultVariables,
        systemName,
      };
      i18n.emit('brandingChanged');
    }
  }, [systemName, i18n]);

  useEffect(() => {
    const favicon = document.querySelector("link[rel~='icon']");
    if (!favicon) return undefined;
    favicon.href = logo;
    favicon.addEventListener('error', onLogoError);
    return () => favicon.removeEventListener('error', onLogoError);
  }, [logo, onLogoError]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === 'system_name' || event.key === 'logo') {
        statusDispatch({
          type: 'branding',
          payload: { [event.key]: event.newValue || '' },
        });
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [statusDispatch]);

  return (
    <BrandingContextV9.Provider value={branding}>
      {children}
    </BrandingContextV9.Provider>
  );
};

export const useSystemBrandingV9 = () => useContext(BrandingContextV9);
